
import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';

export function useOfflineDetection() {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [lastOnlineAt, setLastOnlineAt] = useState<Date | null>(
    navigator.onLine ? new Date() : null
  );
  const [connectionQuality, setConnectionQuality] = useState<'good'|'poor'|'offline'>(
    navigator.onLine ? 'good' : 'offline'
  );

  // Function to check connection quality
  const checkConnectionQuality = useCallback(async () => {
    if (!navigator.onLine) {
      setConnectionQuality('offline');
      return;
    }

    try {
      const startTime = Date.now();
      // Use a tiny request to check connection speed
      const response = await fetch('https://www.google.com/favicon.ico', {
        mode: 'no-cors',
        cache: 'no-store'
      });
      const endTime = Date.now();
      const latency = endTime - startTime;
      
      // If latency is above 1000ms, consider it a poor connection
      setConnectionQuality(latency > 1000 ? 'poor' : 'good');
    } catch (error) {
      console.error("Error checking connection quality:", error);
      // If the request fails, assume offline or very poor connection
      setConnectionQuality('poor');
    }
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      setLastOnlineAt(new Date());
      checkConnectionQuality();
      toast.success('You are back online');
      console.log('App is now online');
    };

    const handleOffline = () => {
      setIsOffline(true);
      setConnectionQuality('offline');
      toast.error('You are offline. Your changes will be saved locally.');
      console.log('App is now offline');
    };

    // Check connection quality periodically when online
    let intervalId: number | null = null;
    if (navigator.onLine) {
      checkConnectionQuality();
      intervalId = window.setInterval(checkConnectionQuality, 30000); // Check every 30 seconds when online
    }

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (intervalId !== null) {
        window.clearInterval(intervalId);
      }
    };
  }, [checkConnectionQuality]);

  return { isOffline, lastOnlineAt, connectionQuality };
}

export default useOfflineDetection;
